import type { Session } from '@/features/auth/types'
import type { CompanySettings, CompanySettingsInput, StageName } from '@/features/settings/types'
import {
  DEFAULT_BRAND_COLOR,
  DEFAULT_DISPLAY_NAME,
  DEFAULT_TERMS,
  TERM_KEYS,
  type TermKey,
} from '@/types/workspace'

/**
 * The eleven contract stages in board order, exactly as `GET /api/session` sends
 * them, so the demo board has the same columns as the real one. Lives here (not
 * `demoAuth.ts`) so `demoAuth` can import it alongside `demoStageNames` without
 * creating an import cycle with `companySettingsApi.ts`.
 */
export const DEMO_STAGES: Session['stages'] = [
  { stage: 'new', stageLabel: 'Neu', stageSection: 'outreach' },
  { stage: 'recontact', stageLabel: 'Erneute Kontaktaufnahme', stageSection: 'outreach' },
  { stage: 'rest_phase', stageLabel: 'Ausgesetzt', stageSection: 'outreach' },
  {
    stage: 'recontact_r2',
    stageLabel: 'Erneute Kontaktaufnahme Runde 2',
    stageSection: 'outreach',
  },
  { stage: 'trial_booked', stageLabel: 'Probebehandlung gebucht', stageSection: 'trial' },
  { stage: 'trial_cancelled', stageLabel: 'Probebehandlung storniert', stageSection: 'trial' },
  {
    stage: 'trial_no_show',
    stageLabel: 'Probebehandlung nicht erschienen',
    stageSection: 'trial',
  },
  { stage: 'trial_attended', stageLabel: 'Probebehandlung stattgefunden', stageSection: 'trial' },
  { stage: 'follow_up', stageLabel: 'Nachfassen', stageSection: 'trial' },
  { stage: 'won_active', stageLabel: 'Gewonnen / Aktiv', stageSection: 'active_customer' },
  { stage: 'exited', stageLabel: 'Ausgeschieden', stageSection: 'closed' },
]

/** Overrides only — what the demo company has set, like the backend's rows. */
const state: {
  displayName: string | null
  brandColor: string | null
  logoUrl: string | null
  terms: Partial<Record<TermKey, { singular: string; plural: string }>>
  stageNames: Record<string, string>
} = {
  displayName: 'Abnehmen im Liegen',
  brandColor: '#68447f',
  logoUrl: null,
  terms: {
    resource: { singular: 'Gerät', plural: 'Geräte' },
    trial: { singular: 'Probebehandlung', plural: 'Probebehandlungen' },
  },
  stageNames: {},
}

export function demoSettings(): CompanySettings {
  const terms = Object.fromEntries(
    TERM_KEYS.map((key) => [
      key,
      {
        singular: state.terms[key]?.singular ?? null,
        plural: state.terms[key]?.plural ?? null,
        defaultSingular: DEFAULT_TERMS[key].singular,
        defaultPlural: DEFAULT_TERMS[key].plural,
      },
    ]),
  ) as CompanySettings['terms']
  return {
    effective: {
      displayName: state.displayName ?? DEFAULT_DISPLAY_NAME,
      brandColor: state.brandColor ?? DEFAULT_BRAND_COLOR,
      logoUrl: state.logoUrl,
      terms: Object.fromEntries(
        TERM_KEYS.map((key) => [key, state.terms[key] ?? DEFAULT_TERMS[key]]),
      ) as CompanySettings['effective']['terms'],
    },
    displayName: { value: state.displayName, defaultValue: DEFAULT_DISPLAY_NAME },
    brandColor: { value: state.brandColor, defaultValue: DEFAULT_BRAND_COLOR },
    terms,
  }
}

export function demoSave(
  request: Omit<CompanySettingsInput, 'terms'> & {
    terms: Partial<Record<TermKey, { singular: string; plural: string }>>
  },
): CompanySettings {
  state.displayName = request.displayName || null
  state.brandColor = request.brandColor || null
  state.terms = request.terms
  return demoSettings()
}

export function demoSetLogo(file: Blob | null): CompanySettings {
  if (state.logoUrl) URL.revokeObjectURL(state.logoUrl)
  state.logoUrl = file ? URL.createObjectURL(file) : null
  return demoSettings()
}

export function demoStageNames(
  defaults: { stage: string; stageLabel: string; stageSection: string }[],
): StageName[] {
  return defaults.map((option) => ({
    stage: option.stage,
    section: option.stageSection,
    name: state.stageNames[option.stage] ?? null,
    defaultName: option.stageLabel,
  }))
}

export function demoSaveStageNames(names: Record<string, string>): void {
  state.stageNames = Object.fromEntries(Object.entries(names).filter(([, name]) => name !== ''))
}
